<template>
  <VBusHelper
    :reactOn="constants.EVENTS.DELIVERYBOOK.MODAL_OPEN"
    :handler="showModal"
  />

  <DialogComponent
    ref="modal"
    dialogId="deliverybook-select-dialog"
    type="bottomsheet"
    noInnerPadding
    @closed="resetForm"
  >
    <div
      class="deliverybook-select-dialog flex flex-col h-[85vh] bg-slate-50 overflow-hidden rounded-t-[32px]"
    >
      <!-- Handle -->
      <div class="flex justify-center pt-3 pb-1 bg-white">
        <div class="w-12 h-1.5 bg-slate-200 rounded-full"></div>
      </div>

      <!-- Header Section -->
      <div class="bg-white border-b border-slate-100 z-4">
        <div class="px-6 py-4">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h2 class="text-xl font-black text-slate-800 tracking-tight">
                Carnet d'adresses
              </h2>
              <p class="text-xs text-slate-500 font-medium">
                Gerez vos adresses et contacts de livraison
              </p>
            </div>
          </div>

          <!-- Tabs -->
          <div class="bg-slate-100/80 rounded-2xl p-1 flex gap-2 mb-4">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="selectTab(tab.id)"
              class="flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition-all duration-300 relative"
              :class="[
                selectedTab === tab.id
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
              ]"
            >
              {{ tab.label }}
              <span
                v-if="tab.count > 0"
                class="ml-1 px-1.5 py-0.5 rounded-full text-[10px]"
                :class="
                  selectedTab === tab.id
                    ? 'bg-white/20 text-white'
                    : 'bg-orange-100 text-orange-600'
                "
              >
                {{ tab.count }}
              </span>
            </button>
          </div>

          <!-- Search Bar -->
          <div class="relative group">
            <div
              class="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-400 group-focus-within:text-orange-500 transition-colors"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              v-model="searchQuery"
              type="search"
              placeholder="Rechercher une adresse ou un contact"
              class="w-full pl-11 pr-4 py-3 bg-slate-50 border-2 border-transparent focus:border-orange-200 focus:bg-white rounded-2xl focus:outline-none transition-all duration-300 text-sm font-medium"
            />
          </div>
        </div>
      </div>

      <!-- List Content -->
      <div class="flex-1 overflow-y-auto p-4 pb-24 relative">
        <!-- Loading State -->
        <div v-if="isLoading" class="space-y-4">
          <div
            v-for="i in 4"
            :key="i"
            class="bg-white rounded-3xl p-4 shadow-sm animate-pulse flex gap-4"
          >
            <div class="w-14 h-14 rounded-2xl bg-slate-100"></div>
            <div class="flex-1 space-y-2">
              <div class="h-4 bg-slate-100 rounded w-1/3"></div>
              <div class="h-3 bg-slate-100 rounded w-3/4"></div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="filteredAddresses.length === 0"
          class="flex flex-col items-center justify-center py-16"
        >
          <div
            class="w-24 h-24 bg-slate-100 rounded-3xl flex items-center justify-center mb-4 text-slate-300 shadow-inner"
          >
            <svg
              class="w-12 h-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
            </svg>
          </div>
          <h3 class="font-bold text-slate-800">{{ emptyStateTitle }}</h3>
          <p class="text-sm text-slate-500 text-center px-8 mt-1">
            {{ emptyStateDescription }}
          </p>
        </div>

        <!-- Items List -->
        <div v-else class="space-y-4">
          <div
            v-for="address in filteredAddresses"
            :key="address.id"
            class="bg-white rounded-[24px] p-4 shadow-sm border-2 transition-all duration-300 group"
            :class="[
              isFromCart && selectedAddressId === address.id
                ? 'border-orange-400 bg-orange-50/30'
                : 'border-white hover:border-slate-100 hover:shadow-md',
              isFromCart ? 'cursor-pointer' : ''
            ]"
            @click="
              isFromCart && selectedTab === 'saved' && selectAddress(address)
            "
          >
            <div class="flex gap-4">
              <!-- Avatar -->
              <div class="flex-shrink-0">
                <div
                  class="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 p-[2px] shadow-sm transform group-hover:scale-105 transition-transform"
                >
                  <div
                    class="w-full h-full rounded-xl bg-white flex items-center justify-center overflow-hidden"
                  >
                    <svg
                      class="w-7 h-7 text-slate-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.5"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <h3 class="font-bold text-slate-800 text-sm leading-tight">
                  {{ address.name || 'Sans nom' }}
                  <span v-if="address.pseudo" class="font-medium text-slate-400"
                    >({{ address.pseudo }})</span
                  >
                </h3>
                <div class="flex items-start gap-1.5 mt-1.5">
                  <svg
                    class="w-3.5 h-3.5 text-orange-400 shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                  </svg>
                  <p
                    class="text-xs text-slate-600 line-clamp-2 leading-relaxed"
                  >
                    {{ address.locationText || 'Adresse non renseignée' }}
                  </p>
                </div>
                <div
                  v-if="address.phoneNumber"
                  class="flex items-center gap-1.5 mt-1.5"
                >
                  <svg
                    class="w-3.5 h-3.5 text-emerald-400 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <p class="text-[11px] font-bold text-slate-500">
                    {{ address.phoneNumber }}
                  </p>
                </div>
                <!-- Abonne Rapidoo Badge -->
                <div
                  v-if="selectedTab === 'saved' && address.isRapidooUser"
                  class="mt-2"
                >
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100"
                  >
                    <svg
                      class="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Abonné Rapidoo
                  </span>
                </div>
              </div>

              <!-- Actions/Indicator -->
              <div
                class="flex-shrink-0 flex flex-col justify-between items-end"
              >
                <div v-if="selectedTab === 'saved'" class="flex gap-1">
                  <!-- Invite Contact Icon -->
                  <button
                    v-if="!address.isRapidooUser"
                    @click.stop="inviteContact(address)"
                    class="p-2 text-slate-300 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition-all"
                    title="Inviter ce contact"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                      />
                    </svg>
                  </button>

                  <!-- Edit Icon -->
                  <button
                    @click.stop="editAddress(address)"
                    class="p-2 text-slate-300 hover:text-orange-500 hover:bg-orange-50 rounded-xl transition-all"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>

                  <button
                    @click.stop="confirmDelete(address)"
                    class="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>

                <!-- Copy Action for Invitations/Requests -->
                <div
                  v-else-if="
                    selectedTab === 'invitations' || selectedTab === 'requests'
                  "
                  class="flex gap-1"
                >
                  <!-- Copy link button -->
                  <button
                    @click.stop="
                      copyLink(
                        selectedTab === 'invitations'
                          ? address.invitationLink
                          : address.locationRequestLink
                      )
                    "
                    class="w-10 h-10 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-all"
                    :title="selectedTab === 'invitations' ? 'Copier le lien d\'invitation' : 'Copier le lien de demande'"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>

                  <!-- Delete button -->
                  <button
                    @click.stop="confirmDelete(address)"
                    class="w-10 h-10 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                    :title="selectedTab === 'invitations' ? 'Supprimer l\'invitation' : 'Supprimer la demande'"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>

                <div
                  v-if="isFromCart && selectedAddressId === address.id"
                  class="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30"
                >
                  <svg
                    class="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    stroke-width="4"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer / FAB -->
      <div class="relative">
        <!-- FAB Overlay -->
        <div
          v-if="fabExpanded"
          class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-30"
          @click="fabExpanded = false"
        ></div>

        <!-- FAB Menu -->
        <div
          class="absolute bottom-24 right-6 z-40 flex flex-col gap-3 items-end overflow-visible"
        >
          <!-- Request -->
          <button
            v-if="fabExpanded"
            @click="openRequestForm"
            class="flex items-center gap-3 bg-white px-5 py-3.5 rounded-2xl shadow-2xl scale-in"
          >
            <span class="text-sm font-black text-slate-700"
              >Demander une adresse</span
            >
            <div
              class="w-10 h-10 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </button>

          <!-- Add -->
          <button
            v-if="fabExpanded"
            @click="addNewAddress"
            class="flex items-center gap-3 bg-white px-5 py-3.5 rounded-2xl shadow-2xl scale-in"
          >
            <span class="text-sm font-black text-slate-700"
              >Ajouter une adresse</span
            >
            <div
              class="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </div>
          </button>

          <!-- Toggle FAB -->
          <button
            class="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-[24px] shadow-2xl shadow-orange-500/40 flex items-center justify-center text-white transform hover:scale-110 active:scale-95 transition-all duration-300 z-50"
            @click="fabExpanded = !fabExpanded"
          >
            <svg
              class="w-8 h-8 transition-transform duration-300"
              :class="{ 'rotate-45': fabExpanded }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              stroke-width="3"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
        </div>

        <!-- Confirm Button -->
        <div
          v-if="isFromCart"
          class="p-4 bg-white border-t border-slate-100 z-20"
        >
          <button
            class="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-2xl font-black shadow-xl shadow-orange-500/30 hover:shadow-2xl hover:shadow-orange-500/40 transition-all active:scale-[0.98] disabled:opacity-30 disabled:grayscale"
            :disabled="!selectedAddressId"
            @click="confirmSelection"
          >
            VALIDER LA POSITION
          </button>
        </div>
      </div>
    </div>
  </DialogComponent>

  <!-- Confirm Delete Local Modal -->
  <div
    v-if="addressToDelete"
    class="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-[500] p-6"
  >
    <div
      class="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl scale-in"
    >
      <div class="p-8 text-center">
        <div
          class="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <svg
            class="w-10 h-10"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </div>
        <h3 class="text-xl font-black text-slate-800 mb-3">
          {{
            addressToDelete?.sourceType === 'invitation'
              ? "Supprimer l'invitation ?"
              : addressToDelete?.sourceType === 'request'
              ? 'Supprimer la demande ?'
              : "Retirer l'adresse ?"
          }}
        </h3>
        <p class="text-sm text-slate-500 font-medium leading-relaxed">
          {{
            addressToDelete?.sourceType === 'invitation'
              ? "Voulez-vous vraiment supprimer l'invitation envoyée à"
              : addressToDelete?.sourceType === 'request'
              ? 'Voulez-vous vraiment supprimer la demande de position pour'
              : 'Voulez-vous vraiment retirer'
          }}
          <b>{{ addressToDelete?.name || 'cet élément' }}</b>
          {{
            addressToDelete?.sourceType === 'invitation' || addressToDelete?.sourceType === 'request'
              ? ' ?'
              : ' de votre carnet ?'
          }}
        </p>
      </div>
      <div class="flex border-t border-slate-100 font-bold">
        <button
          @click="addressToDelete = null"
          class="flex-1 py-5 text-slate-400 hover:bg-slate-50 transition-colors"
        >
          ANNULER
        </button>
        <button
          @click="deleteAddress"
          class="flex-1 py-5 text-red-500 hover:bg-red-50 border-l border-slate-100 transition-colors"
        >
          SUPPRIMER
        </button>
      </div>
    </div>
  </div>

  <!-- Prompt Copy Link Modal -->
  <div
    v-if="promptData"
    class="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-[500] p-6"
  >
    <div
      class="bg-white rounded-[32px] w-full max-w-sm overflow-hidden shadow-2xl scale-in"
    >
      <div class="p-8 text-center">
        <div
          class="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <svg
            class="w-10 h-10"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 class="text-2xl font-black text-slate-800 mb-2">
          {{ promptData.title }}
        </h3>
        <p class="text-sm text-slate-500 mb-6 font-medium">
          {{ promptData.description }}
        </p>
        <div
          class="p-4 bg-slate-50 rounded-2xl mb-6 text-xs text-slate-400 border-2 border-slate-100 break-all"
        >
          {{ promptData.link }}
        </div>
      </div>
      <div class="px-8 pb-8 space-y-3">
        <button
          @click="copyAndClosePrompt"
          class="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-2xl font-black shadow-lg shadow-orange-500/30"
        >
          COPIER LE LIEN
        </button>
        <button
          @click="promptData = null"
          class="w-full py-3 text-slate-400 font-bold"
        >
          PLUS TARD
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import Commons from '@features/shared/libraries/commons';

const modal = ref(null);

// State
const searchQuery = ref('');
const selectedTab = ref('saved');
const selectedAddressId = ref(null);
const addressToDelete = ref(null);
const promptData = ref(null);
const fabExpanded = ref(false);
const isFromCart = ref(false);
const addressBeingEdited = ref(null);

// Store
// Store
import { getStore } from '@core/store';
const deliverybookStore = getStore('deliverybook');
const userStore = getStore('user');
const currentUser = computed(() => userStore?.state?.current);

// Computed
const isLoading = computed(() => deliverybookStore?.isLoading || false);
const allAddresses = computed(() => deliverybookStore?.filteredAddresses || []);

// Tabs configuration
const tabs = computed(() => [
  { id: 'saved', label: 'Sauv.', count: deliverybookStore?.savedCount || 0 },
  {
    id: 'invitations',
    label: 'Invites',
    count: deliverybookStore?.invitationsCount || 0
  },
  {
    id: 'requests',
    label: 'Demandes',
    count: deliverybookStore?.requestsCount || 0
  }
]);

// Filtering logic
const filteredAddresses = computed(() => {
  let list = [];
  if (selectedTab.value === 'saved')
    list = deliverybookStore?.state?.savedAddresses || [];
  else if (selectedTab.value === 'invitations')
    list = deliverybookStore?.state?.invitations || [];
  else if (selectedTab.value === 'requests')
    list = deliverybookStore?.state?.requests || [];

  if (!searchQuery.value) return list;

  const query = searchQuery.value.toLowerCase();
  return list.filter(
    (a) =>
      a.name?.toLowerCase().includes(query) ||
      a.pseudo?.toLowerCase().includes(query) ||
      a.locationText?.toLowerCase().includes(query) ||
      a.phoneNumber?.includes(query)
  );
});

// Empty states
const emptyStateTitle = computed(() => {
  if (selectedTab.value === 'invitations') return 'Aucune invitation';
  if (selectedTab.value === 'requests') return 'Aucune demande';
  return 'Aucune adresse';
});

const emptyStateDescription = computed(() => {
  if (selectedTab.value === 'invitations')
    return "Vous n'avez pas encore envoyé d'invitation.";
  if (selectedTab.value === 'requests')
    return 'Aucune demande de position en cours.';
  return "Votre carnet d'adresses est vide.";
});

// Methods
function showModal(payload = {}) {
  isFromCart.value = payload.isFromCart || false;
  bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESSES_LOAD);
  modal.value?.show();
}

function hideModal() {
  modal.value?.hide();
  bus.emit(constants.EVENTS.DELIVERYBOOK.MODAL_CLOSE);
}

function selectTab(tabId) {
  selectedTab.value = tabId;
  deliverybookStore?.actions?.setSelectedTab(tabId);
}

function selectAddress(address) {
  selectedAddressId.value = address.id;
}

function confirmSelection() {
  const address = (deliverybookStore?.state?.savedAddresses || []).find(
    (a) => a.id === selectedAddressId.value
  );
  if (address) {
    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_SELECT, address);
    hideModal();
  }
}

function editAddress(address) {
  addressBeingEdited.value = address;
  fabExpanded.value = false;
  bus.emit(constants.EVENTS.MAP.LOCATION_SELECTION_REQUIRED, {
    requestKey: 'deliverybook_edit_address',
    defaultPosition: {
      lat: address.location?.lat,
      lng: address.location?.lng,
      address: address.locationText
    },
    defaultValues: {
      name: address.name,
      phoneNumber: address.phoneNumber,
      description: address.location?.description
    },
    additionalFields: [
      {
        key: 'name',
        label: 'Nom du contact',
        type: 'text',
        placeholder: 'Ex: Jean Dupont',
        required: true,
        userDefaultValue: currentUser.value
          ? `${currentUser.value.firstName || ''} ${currentUser.value.lastName || ''}`.trim()
          : ''
      },
      {
        key: 'phoneNumber',
        label: 'Telephone',
        type: 'tel',
        placeholder: 'Ex: 01 23 45 67 89',
        required: true,
        userDefaultValue: currentUser.value?.phoneNumber || ''
      }
    ]
  });
}

function confirmDelete(address) {
  addressToDelete.value = address;
}

function deleteAddress() {
  if (!addressToDelete.value) return;
  bus.emit(
    constants.EVENTS.DELIVERYBOOK.ADDRESS_DELETE,
    addressToDelete.value.id
  );
  if (selectedAddressId.value === addressToDelete.value.id)
    selectedAddressId.value = null;
  addressToDelete.value = null;
}

function addNewAddress() {
  addressBeingEdited.value = null;
  fabExpanded.value = false;
  bus.emit(constants.EVENTS.MAP.LOCATION_SELECTION_REQUIRED, {
    requestKey: 'deliverybook_add_address',
    additionalFields: [
      {
        key: 'name',
        label: 'Nom du contact',
        type: 'text',
        placeholder: 'Ex: Jean Dupont',
        required: true,
        userDefaultValue: currentUser.value
          ? `${currentUser.value.firstName || ''} ${currentUser.value.lastName || ''}`.trim()
          : ''
      },
      {
        key: 'phoneNumber',
        label: 'Telephone',
        type: 'tel',
        placeholder: 'Ex: 01 23 45 67 89',
        required: true,
        userDefaultValue: currentUser.value?.phoneNumber || ''
      }
    ]
  });
}

function openRequestForm() {
  fabExpanded.value = false;
  bus.emit(constants.EVENTS.DELIVERYBOOK.REQUEST_MODAL_OPEN);
}

function inviteContact(address) {
  bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_INVITE_CREATE, {
    name: address.name,
    pseudo: address.pseudo || '',
    phoneNumber: address.phoneNumber || ''
  });
}

function copyLink(link, direct = false) {
  if (!link) return;

  const text = link;
  const cb = () =>
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'success',
      title: 'Lien copie',
      body: 'Partagez-le avec votre contact !'
    });

  Commons.copyToClipboard(text, cb, (err) => {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur de copie',
      body: 'Partagez-le avec votre contact !'
    });
    console.log(err);
  });
}

function fallbackCopy(text, cb) {
  const el = document.createElement('textarea');
  el.value = text;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
  cb();
}

function copyAndClosePrompt() {
  if (!promptData.value) return;
  copyLink(promptData.value.link);
  selectTab(promptData.value.tab);
  promptData.value = null;
}

function resetForm() {
  selectedAddressId.value = null;
  fabExpanded.value = false;
  searchQuery.value = '';
  addressBeingEdited.value = null;
}

// Global Event Handlers
function onLocationSelected(data) {
  if (data.requestKey === 'deliverybook_add_address') {
    const loc = data.selectedLocation;
    const addressData = {
      ...loc,
      name: loc.name || 'Nouvelle adresse',
      phoneNumber: loc.phoneNumber || '',
      description: loc.description || ''
    };
    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATE, addressData);
  } else if (
    data.requestKey === 'deliverybook_edit_address' &&
    addressBeingEdited.value
  ) {
    const loc = data.selectedLocation;
    const updates = {
      ...loc,
      name: loc.name,
      phoneNumber: loc.phoneNumber,
      description: loc.description
    };
    bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_UPDATE, {
      addressId: addressBeingEdited.value.id,
      updates
    });
    addressBeingEdited.value = null;
  }
}

function onInviteCreated(data) {
  if (data.invite?.invitationLink) {
    promptData.value = {
      title: 'Invitation creee !',
      description:
        "Envoyez ce lien a votre contact pour qu'il rejoigne Rapidoo.",
      link: data.invite.invitationLink,
      tab: 'invitations'
    };
  }
}

function onRequestCreated(data) {
  if (data.request?.locationRequestLink) {
    promptData.value = {
      title: 'Demande creee !',
      description:
        'Partagez ce lien pour que votre contact nous renseigne sa position.',
      link: data.request.locationRequestLink,
      tab: 'requests'
    };
  }
}

onMounted(() => {
  bus.on('map_location_selected', onLocationSelected);
  bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_INVITE_CREATED, onInviteCreated);
  bus.on(
    constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATED,
    onRequestCreated
  );
});

onBeforeUnmount(() => {
  bus.off('map_location_selected', onLocationSelected);
  bus.off(
    constants.EVENTS.DELIVERYBOOK.ADDRESS_INVITE_CREATED,
    onInviteCreated
  );
  bus.off(
    constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATED,
    onRequestCreated
  );
});
</script>

<style scoped>
.scale-in {
  animation: scale-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

@keyframes scale-in {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.deliverybook-select-dialog {
  box-shadow: 0 -20px 40px rgba(0, 0, 0, 0.1);
}

/* Hide scrollbar but keep functionality */
.overflow-y-auto::-webkit-scrollbar {
  display: none;
}
.overflow-y-auto {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
